import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/**
 * Reusable one-tap copy-to-clipboard button.
 *
 * <CopyButton value={code} label="Copy code" toastMsg="Referral code copied" />
 *
 * Renders as an icon-button that flips to a green check for 1.6s on click,
 * fires a Sonner toast, and gracefully no-ops in browsers without the
 * clipboard API. Every instance auto-gets `data-testid="copy-btn-<value>"`
 * unless overridden.
 */
export default function CopyButton({
    value,
    label,
    toastMsg = "Copied to clipboard",
    size = "sm",
    variant = "ghost",
    className = "",
    testId,
    iconOnly = false,
}) {
    const [copied, setCopied] = useState(false);

    const onClick = async () => {
        if (!value) return;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(String(value));
            } else {
                // Legacy fallback for browsers without the async clipboard API
                const ta = document.createElement("textarea");
                ta.value = String(value);
                ta.setAttribute("readonly", "");
                ta.style.position = "absolute";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setCopied(true);
            toast.success(toastMsg);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            toast.error("Couldn't copy — please copy manually");
        }
    };

    return (
        <Button
            type="button"
            size={size}
            variant={variant}
            onClick={onClick}
            className={`gap-1.5 ${className}`}
            data-testid={testId || `copy-btn-${String(value).slice(0, 12)}`}
            aria-label={label || toastMsg}
        >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
            {!iconOnly && <span className="text-xs">{copied ? "Copied" : (label || "Copy")}</span>}
        </Button>
    );
}
